import * as vp from 'vopay';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast } from 'vopay';
import usePostUpdateWebhook from '../../../../hooks/useApi/usePostUpdateWebhook';
import usePostResendWebhook from '../../../../hooks/useApi/usePostResendWebhook';

import useFetchWebhookInfo from '../../../../hooks/useApi/useFetchWebhookInfo';
import UpdateWebhooksForm from './UpdateWebhooksForm';
import UpdateWebhooksReview from './UpdateWebhooksReview';
import UpdateWebhookContext, { UpdateWebhookCtx } from './Dialogue/UpdateWebhookContext';
import RetriggerWebhookContext, { RetriggerWebhookResponseCtx } from './Dialogue/RetriggerWebhookResponseContext';

import UpdateWebhookDialogue from './Dialogue/UpdateWebhookDialogue';
import {
  UpdateWebhookDialogueHandler,
  DialogueHandlerProps,
  RetriggerWebhookResponseDialogueHandler,
} from './Dialogue/types';
import { usePartnerData } from '../../../../providers/PartnerData.provider';
import useDataFetchIndicator from '../../../../hooks/useDataFetchIndicator';
import RetriggerWebhookResponseDialogue from './Dialogue/RetriggerWebhookResponseDialogue';
import RetriggerWebhookDialogue from '../Dialogue/RetriggerWebhookDialogue';
import { WebhookType } from './types';

type FC = FunctionComponent;

const UpdateWebhookContainer: FC = () => {
  const reviewRef = React.createRef<HTMLDivElement>();
  const history = useHistory();
  const [UpdateError, setUpdateError] = useState('');
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [showUpdateErrorAlert, setShowUpdateErrorAlert] = useState(false);
  const [showUpdateSuccessAlert, setShowUpdateSuccessAlert] = useState(false);
  const [shouldUpdateWebhook, setShouldUpdateWebhook] = useState(false);
  const [updateWebhookResponse, , updateWebhook] = usePostUpdateWebhook({});
  const [retriggerWebookResponse, postRetriggerWebhook] = usePostResendWebhook({});
  const [webhookDetails, getWebhookDetails] = useFetchWebhookInfo({});
  const [showRetriggerWebhookErrorAlert, setShowRetriggerWebhookErrorAlert] = useState(false);
  const [showRetriggerSuccessAlert, setShowRetriggerSuccessAlert] = useState(false);
  const [shouldRetriggerWebhook, setShouldRetriggerWebhook] = useState(false);
  const [retriggerError, setRetriggerError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [showToast, shouldShowToast] = useState(false);
  const [validatedWebhookEvents, setValidatedWebhookEvents] = useState<vp.WebhookEvent[]>([]);
  const [webhookEventType, setWebhookEventType] = useState({
    label: '',
    id: '',
  });
  const [defaultValues, setDefaultValues] = useState({
    accountonboarding: '',
  });
  const [toggleValues, setToggleValues] = useState({
    accountonboarding: false,
  });
  const [reviewing, setReviewing] = useState(false);
  const { response } = usePartnerData();
  const [webHookRequests, setWebhookRequests] = useState<vp.UpdatePartnerWebhookRequest[]>([
    {
      WebHookUrl: webhookDetails?.Webhooks ? webhookDetails?.Webhooks[0]?.Url : '',
      Disabled: false,
      Username: response.loginUsername,
      Type: 'accountonboarding',
    },
  ]);
  useEffect(() => {
    getWebhookDetails({
      Username: response.loginUsername,
    });
  }, []);
  useEffect(() => {
    if (webhookDetails?.Success) {
      const events: vp.WebhookEvent[] = [];

      Object.keys(webhookDetails?.Webhooks || {}).forEach((key) => {
        const event = webhookDetails?.Webhooks[key];
        if (event.Url !== '') {
          events.push(event.Event);
        }
      });
      if (events.length > 0) {
        setValidatedWebhookEvents(events);
      }
    }

    Object.values(webhookDetails?.Webhooks || {}).forEach((obj) => {
      switch (obj.Event) {
        case 'accountonboarding':
          setDefaultValues((prevValues) => ({
            ...prevValues,
            accountonboarding: obj.Url,
          }));
          setToggleValues((prevValues) => ({
            ...prevValues,
            accountonboarding: !!obj.Url,
          }));
          break;
        default:
          break;
      }
    });
    setWebhookRequests([
      {
        WebHookUrl: webhookDetails?.Webhooks ? webhookDetails?.Webhooks[0]?.Url : '',
        Disabled: false,
        Username: response.loginUsername,
        Type: 'accountonboarding',
      },
    ]);
  }, [webhookDetails]);

  const UpdateWebhook: UpdateWebhookCtx = {
    showUpdateSuccessAlert,
    showUpdateErrorAlert,
    UpdateError,
  };
  const RetriggerWebhookResponse: RetriggerWebhookResponseCtx = {
    showRetriggerSuccessAlert,
    retriggerError,
    showRetriggerWebhookErrorAlert,
  };
  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<UpdateWebhookDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: UpdateWebhookDialogueHandler = {
        update: {
          success: {
            default: () => {
              setShowUpdateSuccessAlert(false);
              history.push('/settings/webhooks');
            },
          },
          error: {
            default: () => {
              setShowUpdateErrorAlert(false);
              setUpdateError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },

    handleRetriggerDialogue: (handlerProps: DialogueHandlerProps<RetriggerWebhookResponseDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: RetriggerWebhookResponseDialogueHandler = {
        error: {
          success: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
              history.push('/settings/webhooks/update');
            },
          },
          error: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
            },
          },
        },
        alert: {
          success: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
              history.push('/settings/webhooks/update');
            },
          },
          error: {
            default: () => {
              setShowRetriggerWebhookErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },

    onReview: () => {
      setReviewing(true);
      setWebhookRequests([
        {
          WebHookUrl: toggleValues.accountonboarding ? defaultValues.accountonboarding : '',
          Disabled: !toggleValues.accountonboarding,
          Username: response.loginUsername,
          Type: 'accountonboarding',
        },
      ]);
    },
    onUpdate: async () => {
      try {
        const interval = setInterval(() => {
          setDataFetchingInfo(false, 'Loading...', 'fullScreen');
          setDataFetchingInfo(true, 'Loading...', 'fullScreen');
        }, 150);
        const results = await Promise.all(
          webHookRequests.map(async (req) => {
            try {
              await updateWebhook(req);
              return { success: true };
            } catch (error) {
              return { success: false };
            }
          }),
        );
        let isSuccess = true;
        results.forEach((result) => {
          if (!result.success) {
            isSuccess = false;
          }
        });

        if (isSuccess) {
          clearInterval(interval);
          setDataFetchingInfo(false, 'Loading...', 'fullScreen');
          setShowUpdateSuccessAlert(true);
        } else {
          setDataFetchingInfo(false, 'Loading...', 'fullScreen');
          setUpdateError(updateWebhookResponse?.ErrorMessage || '');
          setShowUpdateErrorAlert(true);
        }
      } catch (error) {
        console.error('Error making webhook requests: ', error);
      }
    },
    onBack: () => {
      setReviewing(false);
    },

    setWebhookType: (value: WebhookType) => {
      setWebhookEventType({
        label: value.label,
        id: value.id,
      });
    },
    retriggerWebhook: (value: vp.RetriggerWebhookRequest) => {
      setShouldRetriggerWebhook(true);
      shouldShowToast(true);
      postRetriggerWebhook(value);
    },
  };

  useEffect(() => {
    if (shouldUpdateWebhook) {
      setShouldUpdateWebhook(false);

      if (updateWebhookResponse?.Success === true) {
        setShowUpdateSuccessAlert(true);
      } else {
        setUpdateError(updateWebhookResponse?.ErrorMessage || '');
        setShowUpdateErrorAlert(true);
      }
    }
  }, [updateWebhookResponse]);

  useEffect(() => {
    if (showToast) {
      shouldShowToast(false);
      setIsVisible(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (shouldRetriggerWebhook === true) {
      const { Success, ErrorMessage } = retriggerWebookResponse;

      if (Success === true) {
        setShowRetriggerSuccessAlert(true);
      } else if (Success === false) {
        setShowRetriggerWebhookErrorAlert(true);
        setRetriggerError(ErrorMessage || '');
      }

      setShouldRetriggerWebhook(false);
    }
  }, [retriggerWebookResponse]);

  useEffect(() => {
    if (!reviewing || !reviewRef.current) return;

    reviewRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [reviewing]);

  return (
    <div ref={reviewRef}>
      <UpdateWebhookContext.Provider value={UpdateWebhook}>
        <RetriggerWebhookContext.Provider value={RetriggerWebhookResponse}>
          {!reviewing && (
            <UpdateWebhooksForm
              validatedWebhookEvents={validatedWebhookEvents}
              handlers={handlers}
              defaultValues={defaultValues}
              toggleValues={toggleValues}
              setDefaultValues={setDefaultValues}
              setToggleValues={setToggleValues}
              onSubmit={handlers.onReview}
              setIsVisible={setIsVisible}
            />
          )}
          {reviewing && (
            <UpdateWebhooksReview
              defaultValues={defaultValues}
              toggleValues={toggleValues}
              onSubmit={handlers.onUpdate}
              onBack={handlers.onBack}
            />
          )}
          <UpdateWebhookDialogue handleDialogue={handlers.handleDialogue} />
          <RetriggerWebhookResponseDialogue handleDialogue={handlers.handleRetriggerDialogue} />
        </RetriggerWebhookContext.Provider>
      </UpdateWebhookContext.Provider>
      <RetriggerWebhookDialogue
        handlers={handlers}
        title="Retrigger Webhook"
        variant="webhook-retrigger"
        isVisible={isVisible}
        webhookEventType={webhookEventType}
        closeModal={() => setIsVisible(false)}
      />
      <Toast
        label={
          <>
            <span style={{ fontWeight: 700 }}>{webhookEventType.label} webhook retriggered</span>
          </>
        }
        isVisible={showToast}
      />
    </div>
  );
};

export default UpdateWebhookContainer;
